import {
  Button as AIMEButton,
  Input as AIMEInput,
} from "tot-loft-component-library";

import * as PRTNR from "./prtnr";

import styled from "@emotion/styled";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { InfoCircleIcon } from "./assets";
import GenericModal from "./GenericModal";
import ModalReferralstwo from "./ModalReferralstwo";
import { fetchReferralsItems } from "./redux/slices/ReferralsThunks";
import ReferralCard from "./ReferralCard";
import { resetUpdateSuccess } from "./redux/slices/ReferralsSlice";

const tableHeaders = [
  { label: "Referral ID", flex: true },
  { label: "Referrer Name", flex: true },
  { label: "Potential Client's Website", width: "20%" },
  { label: "Expected/Actual Close Date", width: "25%" },
  { label: "Status", flex: true, type: "status" },
  { label: "Action", flex: true, align: "center" },
];

const tableColumns = [
  { key: "referrer_id", flex: true },
  { key: "memberName", flex: true },
  { key: "company_website_url", width: "20%" },
  { key: "expected_close_date",  width: "25%" },
  { key: "referral_status", flex: true, type: "status" },
  { key: "action", flex: true, align: "center" },
];

const statusCounts = {
  "Lead Identified": 0,
  Opportunity: 0,
  "Proposal Sent": 0,
  "Verbally Committed": 0,
  Won: 0,
  Lost: 0,
};

// Custom hooks for better separation of concerns
const useVendorData = () => {
  const [vendor, setVendor] = useState(null);

  useEffect(() => {
    try {
      const vendorData = localStorage.getItem("vendor");
      if (vendorData) {
        setVendor(JSON.parse(vendorData));
      }
    } catch (error) {
      console.error("Failed to parse vendor data:", error);
    }
  }, []);

  return vendor;
};

const filterReferrals = (referrals, searchTerm) => {
  if (!searchTerm.trim()) return referrals;

  const searchableKeys = [
    "referrer_id",
    "memberName",
    "company_website_url",
    "expected_close_date",
    "referral_status",
  ];

  const terms = searchTerm.toLowerCase().split(/\s+/);

  return referrals.filter((item) => {
    return terms.every((term) =>
      searchableKeys.some((key) => {
        const value = item[key]?.toLowerCase() || "";
        return value.includes(term);
      })
    );
  });
};

const sortReferralsByDate = (referrals) => {
  return [...referrals].sort((a, b) => {
    const dateA = new Date(a.expected_close_date);
    const dateB = new Date(b.expected_close_date);
    return dateA - dateB;
  });
};

const calculateStatusCounts = (referrals) => {
  return referrals.reduce((acc, curr) => {
    const status = curr?.referral_status || "Unknown";
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});
};

// Main Dashboard Component
export default function Dashboard() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const vendor = useVendorData();
  const referrals = useSelector((state) => state.referrals.items);

  const referralsLoading = useSelector((state) => state.referrals.loading);
  const [enrichedReferrals, setEnrichedReferrals] = useState([]);
  const updateSuccess = useSelector((state) => state.referrals.updateSuccess);

  
  // Local state
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (!referrals.length) return;

    const enriched = referrals.map((ref) => ({
      ...ref,
      memberName: `${ref.member_id.first_name} ${ref.member_id.last_name}`,
      referrer_id: "R" + ref._id.slice(-7),
    }));

    setEnrichedReferrals(enriched);
  }, [referrals]);

  const filteredAndSortedReferrals = useMemo(() => {
    try {
      const filtered = filterReferrals(enrichedReferrals, searchTerm);
      return sortReferralsByDate(filtered);
    } catch (error) {
      console.error("Error filtering referrals:", error);
      return [];
    }
  }, [enrichedReferrals, searchTerm]);

  const statusCounts = useMemo(() => {
    try {
      return calculateStatusCounts(referrals);
    } catch (error) {
      console.error("Error calculating status counts:", error);
      return {};
    }
  }, [referrals]);

  // Event handlers
  const handleEditClick = useCallback((referral) => {
    setSelectedReferral(referral);
    setIsModalOpen(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    setSelectedReferral(null);
  }, []);

  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  const handleViewAllClick = useCallback(() => {
    navigate("/referrals");
  }, [navigate]);

  // Data fetching effect
  useEffect(() => {
    if (vendor?._id) {
      try {
        dispatch(fetchReferralsItems(vendor._id));
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    }
  }, [dispatch, vendor?._id]);

  useEffect(() => {
    if (updateSuccess && vendor?._id) {
      // Data already updated in Redux, just fetch history
      dispatch(fetchReferralsItems(vendor._id));
      // Reset the flag
      dispatch(resetUpdateSuccess());
    }
  }, [updateSuccess, vendor?._id, dispatch]);

  // Early returns for loading and error states
  if (referralsLoading) {
    return (
      <RootWrapperHome>
        <DashboardWrapper>
          <div>Loading dashboard data...</div>
        </DashboardWrapper>
      </RootWrapperHome>
    );
  }

  if (!vendor) {
    return (
      <RootWrapperHome>
        <DashboardWrapper>
          <div>Unable to load vendor information</div>
        </DashboardWrapper>
      </RootWrapperHome>
    );
  }

  const displayReferrals = filteredAndSortedReferrals.slice(0, 4);

  return (
    <RootWrapperHome>
      <DashboardWrapper>
        <Wrapper>
          <DashboardHeader>
            <Frame1462>
              <WelcomeJames>Welcome {vendor?.company_name}</WelcomeJames>
            </Frame1462>
          </DashboardHeader>

          <QuickAnalytics>
            <Frame1423>
              <Frame1422>
                <PRTNR.StatusBadge status="All" />
                <Frame1429>
                  <TotalEarnings>
                    All <br />
                    Referrals
                  </TotalEarnings>
                  <_1250>{referrals.length}</_1250>
                </Frame1429>
              </Frame1422>
            </Frame1423>

            <Frame1423>
              <Frame1422>
                <PRTNR.StatusBadge status="Lead identified" />
                <Frame1429>
                  <Frame5>
                    <TooltipWrapper>
                      <GFeildsInfo src={InfoCircleIcon} alt="icon" />
                      <TooltipText className="tooltip">
                        A potential buyer has been referred, but not qualified
                        by the Vendor yet.
                      </TooltipText>
                    </TooltipWrapper>
                    <TotalEarnings_0001>Lead Identified</TotalEarnings_0001>
                  </Frame5>
                  <_1250>{statusCounts["Lead Identified"] || 0}</_1250>
                </Frame1429>
              </Frame1422>
            </Frame1423>

            <Frame1429_0002>
              <Frame1422>
                <PRTNR.StatusBadge status="Opportunity" />
                <Frame1429>
                  <Frame5>
                    <TooltipWrapper>
                      <GFeildsInfo src={InfoCircleIcon} alt="icon" />
                      <TooltipText className="tooltip">
                        Qualified interest confirmed; there is ICP fit and a
                        real chance to win business.
                      </TooltipText>
                    </TooltipWrapper>
                    <TotalEarnings_0001>Opportunity</TotalEarnings_0001>
                  </Frame5>
                  <_1250>{statusCounts["Opportunity"] || 0}</_1250>
                </Frame1429>
              </Frame1422>
            </Frame1429_0002>

            <Frame1423>
              <Frame1422>
                <PRTNR.StatusBadge status="Proposal sent" />
                <Frame1429>
                  <Frame5>
                    <TooltipWrapper>
                      <GFeildsInfo src={InfoCircleIcon} alt="icon" />
                      <TooltipText className="tooltip">
                        A formal offer/solution with pricing has been delivered
                        to the buyer.
                      </TooltipText>
                    </TooltipWrapper>
                    <TotalEarnings_0001>
                      Proposal <br />
                      sent
                    </TotalEarnings_0001>
                  </Frame5>
                  <_1250>{statusCounts["Proposal Sent"] || 0}</_1250>
                </Frame1429>
              </Frame1422>
            </Frame1423>

            <Frame1423>
              <Frame1422>
                <PRTNR.StatusBadge status="Verbally Committed" />
                <Frame1429>
                  <Frame5>
                    <TooltipWrapper>
                      <GFeildsInfo src={InfoCircleIcon} alt="icon" />
                      <TooltipText className="tooltip">
                        Buyer has given non-binding confirmation they intend to
                        proceed, contracting is in process
                      </TooltipText>
                    </TooltipWrapper>
                    <TotalEarnings_0001>Verbally committed</TotalEarnings_0001>
                  </Frame5>
                  <_1250>{statusCounts["Verbally Committed"] || 0}</_1250>
                </Frame1429>
              </Frame1422>
            </Frame1423>

            <Frame1423>
              <Frame1422>
                <PRTNR.StatusBadge status="Won" />
                <Frame1429>
                  <Frame5>
                    <TooltipWrapper>
                      <GFeildsInfo src={InfoCircleIcon} alt="icon" />
                      <TooltipText className="tooltip">
                        Contract closed and deal officially secured
                      </TooltipText>
                    </TooltipWrapper>
                    <TotalEarnings_0005>Won</TotalEarnings_0005>
                  </Frame5>
                  <_1250>{statusCounts["Won"] || 0}</_1250>
                </Frame1429>
              </Frame1422>
            </Frame1423>

            <Frame1428_0006>
              <Frame1422>
                <PRTNR.StatusBadge status="Lost" />
                <Frame1429>
                  <Frame5>
                    <TooltipWrapper>
                      <GFeildsInfo src={InfoCircleIcon} alt="icon" />
                      <TooltipText className="tooltip">
                        Opportunity closed without success; no deal
                      </TooltipText>
                    </TooltipWrapper>
                    <TotalEarnings_0005>Lost</TotalEarnings_0005>
                  </Frame5>
                  <_1250>{statusCounts["Lost"] || 0}</_1250>
                </Frame1429>
              </Frame1422>
            </Frame1428_0006>
          </QuickAnalytics>

          <DashboardData>
            <CardWrapper>
              <Frame1433>
                <Frame1431_0001>
                  <Frame1431_0002>
                    <RecentReferrals>Recent Referrals</RecentReferrals>
                  </Frame1431_0002>
                </Frame1431_0001>
                <Frame1432>
                  <GFieldsStacked>
                    <GFieldsStacked_0001>
                      <PRTNR.Input
                        type="text"
                        placeholder="Search"
                        value={searchTerm}
                        onChange={handleSearchChange}
                      />
                    </GFieldsStacked_0001>
                  </GFieldsStacked>
                </Frame1432>
              </Frame1433>

              {filteredAndSortedReferrals.map((item, index) => (
                <ReferralCard
                  key={item._id || index}
                  item={item}
                  handleEditClick={handleEditClick}
                />
              ))}
            </CardWrapper>

            <Left_0001>
              <TableReferrals>
                <Frame1433>
                  <Frame1431_0001>
                    <Frame1431_0002>
                      <RecentReferrals>Recent Referrals</RecentReferrals>
                    </Frame1431_0002>
                  </Frame1431_0001>
                  <Frame1432>
                    <GFieldsStacked>
                      <GFieldsStacked_0001>
                        <AIMEInput
                          type="text"
                          placeholder="Search"
                          value={searchTerm}
                          onChange={handleSearchChange}
                        />
                      </GFieldsStacked_0001>
                    </GFieldsStacked>
                  </Frame1432>
                </Frame1433>

                <ScrollContainer>
                  <PRTNR.TableComponent
                    data={displayReferrals}
                    tableColumns={tableColumns}
                    tableHeaders={tableHeaders}
                    handleEditClick={handleEditClick}
                    isLoading={referralsLoading}
                  />
                </ScrollContainer>

                {filteredAndSortedReferrals.length > 0 && (
                  <Frame1460>
                    <AIMEButton
                      size="small"
                      onClick={handleViewAllClick}
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        flexDirection: "row",
                        alignItems: "center",
                        flex: "none",
                        borderRadius: "999px",
                        width: "168px",
                        height: "36px",
                        background: "rgb(241, 245, 249)",
                        backgroundRepeat: "no-repeat",
                        backgroundSize: "contain",
                        backgroundPosition: "center",
                        boxSizing: "border-box",
                        padding: "8px 16px",
                        "&:hover": {
                          background: "rgb(241, 245, 249)",
                          opacity: "0.9",
                        },
                      }}
                    >
                      <span
                        style={{
                          color: "rgb(33, 37, 67)",
                          textOverflow: "ellipsis",
                          fontSize: "14px",
                          fontFamily: "Montserrat, sans-serif",
                          fontWeight: "600",
                          lineHeight: "140%",
                          textAlign: "left",
                        }}
                      >
                        View all
                      </span>
                    </AIMEButton>
                  </Frame1460>
                )}
              </TableReferrals>
            </Left_0001>
          </DashboardData>
        </Wrapper>
      </DashboardWrapper>

      <GenericModal isOpen={isModalOpen} onClose={handleModalClose}>
        <ModalReferralstwo
          isOpen={isModalOpen}
          onClose={handleModalClose}
          referral={selectedReferral}
        />
      </GenericModal>
    </RootWrapperHome>
  );
}
const CardWrapper = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex; /* Show cards only on mobile */
    flex-direction: column;
    gap: 24px;
    width: 100%;
  }
`;
const RootWrapperHome = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: center;
  background: rgba(82, 13, 13, 1);
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;
  width: 100%;
`;
const DashboardWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  background: rgb(241, 245, 249);
  box-sizing: border-box;
  padding-inline: clamp(1rem, 5vw, 3.75rem);
  flex-shrink: 0;
  width: 100%;
`;

const Wrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: center;
  flex: 1;
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  box-sizing: border-box;
  padding: 0px 0px 32px;
`;

const DashboardHeader = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  box-sizing: border-box;
  padding: 32px 0px;
  flex-shrink: 0;
`;

const Frame1462 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  gap: 4px;
  width: 1010px;
  box-sizing: border-box;
  @media (max-width: 1024px) and (min-width: 768px) {
    width: 100%;
  }

  @media (max-width: 767px) {
    width: 100%;
  }
`;

const WelcomeJames = styled.span`
  color: rgb(33, 37, 67);
  text-overflow: ellipsis;
  font-size: clamp(1.5rem, 5vw, 3rem);
  font-family: Montserrat, sans-serif;
  font-weight: 600;
  letter-spacing: clamp(-0.03rem, -0.05vw, -0.06rem);
  text-align: left;
  @media (max-width: 768px) {
    white-space: normal; /* allow wrapping on smaller screens */
  }
`;

const Frame1461 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: flex-start;
  flex: none;
  gap: 8px;
  box-sizing: border-box;
`;

const QuickAnalytics = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  flex-wrap: wrap;
`;

const Frame1423 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  min-width: 158px;
  box-sizing: border-box;
`;

const Frame1422 = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  box-shadow: 0px 1px 2px rgba(10, 13, 20, 0.03);
  border: solid 1px rgb(225, 228, 234);
  border-radius: 16px;
  align-self: stretch;
  background: rgb(255, 255, 255);
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;
  padding: 16px;
  flex-shrink: 0;
`;

const Frame1429 = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  align-items: flex-start;
  flex: none;
  height: 85px;
  box-sizing: border-box;
`;

const TotalEarnings = styled.span`
  color: rgb(100, 116, 139);
  text-overflow: ellipsis;
  font-size: clamp(14px, 2.5vw, 18px);
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 121%;
  text-align: center;
  align-self: stretch;
  flex-shrink: 0;
`;

const _1250 = styled.span`
  color: rgb(33, 37, 67);
  text-overflow: ellipsis;
  font-size: 30px;
  font-family: Montserrat, sans-serif;
  font-weight: 700;
  text-align: center;
  align-self: stretch;
  flex-shrink: 0;
`;

const TotalEarnings_0001 = styled.span`
  color: rgb(100, 116, 139);
  text-overflow: ellipsis;
  font-size: clamp(14px, 2.5vw, 18px);
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 121%;
  text-align: center;
`;

const Frame1429_0002 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  width: 158px;
  box-sizing: border-box;
`;

const Frame1428_0003 = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  flex: none;
  gap: 4px;
  border-radius: 999px;
  width: 48px;
  height: 48px;
  background: linear-gradient(
    -90deg,
    rgba(244, 186, 52, 0.1),
    rgba(244, 186, 52, 0.1)
  );
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;
`;

const BadgeDollar_0003 = styled.span`
  color: rgb(244, 186, 52);
  text-overflow: ellipsis;
  font-size: 24px;
  font-family: "Font Awesome 6 Pro", sans-serif;
  font-weight: 400;
  line-height: 160%;
  text-align: center;
`;

const Frame1428_0005 = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  flex: none;
  gap: 4px;
  border-radius: 999px;
  width: 48px;
  height: 48px;
  background: linear-gradient(
    -90deg,
    rgba(18, 124, 78, 0.1),
    rgba(18, 124, 78, 0.1)
  );
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;
`;

const BadgeDollar_0005 = styled.span`
  color: rgb(18, 124, 78);
  text-overflow: ellipsis;
  font-size: 24px;
  font-family: "Font Awesome 6 Pro", sans-serif;
  font-weight: 400;
  line-height: 160%;
  text-align: center;
`;

const TotalEarnings_0005 = styled.span`
  color: rgb(100, 116, 139);
  text-overflow: ellipsis;
  font-size: clamp(14px, 2.5vw, 18px);
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 121%;
  text-align: center;
  min-height: 48px;
  flex: 1;
`;

const Frame1428_0006 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  width: 158px;
  box-sizing: border-box;
`;

const Frame1428_0007 = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  flex: none;
  gap: 4px;
  border-radius: 999px;
  width: 48px;
  height: 48px;
  background: linear-gradient(
    -90deg,
    rgba(240, 70, 63, 0.1),
    rgba(240, 70, 63, 0.1)
  );
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;
`;

const BadgeDollar_0006 = styled.span`
  color: rgb(240, 70, 63);
  text-overflow: ellipsis;
  font-size: 24px;
  font-family: "Font Awesome 6 Pro", sans-serif;
  font-weight: 400;
  line-height: 160%;
  text-align: center;
`;

const DashboardData = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: flex-start;
  gap: 24px;
  align-self: stretch;
  box-sizing: border-box;
  padding: 24px 0px;
  flex-shrink: 0;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  flex-wrap: wrap;
`;

const Left_0001 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  gap: 16px;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  box-sizing: border-box;
  @media (max-width: 768px) {
    display: none;
  }
`;

const TableReferrals = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  box-shadow: 0px 1px 2px rgba(10, 13, 20, 0.03);
  border: solid 1px rgb(225, 228, 234);
  border-radius: 16px;
  align-self: stretch;
  background: rgb(255, 255, 255);
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;
  padding: 24px;
  flex-shrink: 0;
`;

const Frame1433 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: flex-start;
  gap: 20px;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
  flex-wrap: wrap;
`;

const Frame1431_0001 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: center;
  flex: 1;
`;

const Frame1431_0002 = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: row;
  align-items: center;
  flex: 1;
  gap: 8px;
  width: 912px;
  box-sizing: border-box;
  @media (max-width: 1024px) and (min-width: 768px) {
    width: 100%;
  }

  @media (max-width: 767px) {
    width: 100%;
  }
`;

const RecentReferrals = styled.span`
  color: rgb(33, 37, 67);
  text-overflow: ellipsis;
  font-size: 18px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: left;
  min-height: 29px;
  flex: 1;
`;

const ScrollContainer = styled.div`
  overflow-x: auto;
  width: 100%;
`;

const Frame1432 = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: row;
  align-items: center;
  flex: none;
  gap: 8px;
  box-sizing: border-box;
`;

const GFieldsStacked = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  flex: none;
  box-sizing: border-box;
`;

const GFieldsStacked_0001 = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: flex-start;
  flex: none;
  gap: 4px;
  box-sizing: border-box;
  width: 200px;
`;

const Frame1460 = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const Frame5 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  gap: 4px;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const GFeildsInfo = styled.img`
  width: 18px;
  height: 18px;
  object-fit: cover;
`;

const TooltipWrapper = styled.div`
  position: relative;
  display: inline-block;

  &:hover .tooltip {
    visibility: visible;
    opacity: 1;
  }
`;

const TooltipText = styled.div`
  visibility: hidden;
  color: #000000;
  font-weight: 500;
  font-size: 12px;
  text-align: start;
  padding: 8px 16px;
  border-radius: 4px;
  position: absolute;
  z-index: 1;
  top: 125%; /* Show below the icon */
  left: 50%;
  transform: translateX(-50%);
  opacity: 0;
  transition: opacity 0.3s;
  max-width: 280px;
  width: 280px;
  backdrop-filter: blur(20px);
  background: var(--backgrounds-primary, #ffffff);
  font-family: "Montserrat", sans-serif;
  &::after {
    content: "";
    position: absolute;
    bottom: 100%; /* Top of the tooltip box */
    left: 50%;
    margin-left: -6px;
    border-width: 6px;
    border-style: solid;
    border-color: transparent transparent var(--backgrounds-primary, #ffffff)
      transparent;
  }
`;
